<template>
  <div>
    <!-- 对齐快捷操作 -->
    <div
      class="flex justify-between h-12 items-center px-4"
      :class="{
        ' opacity-30 cursor-not-allowed':
          attrDisabled || isPhotoAlbumBg || isVideoWatchfacce || isSlotFollowing
      }"
    >
      <div
        v-for="item in alignItems"
        :key="item.align"
        @click="
          () =>
            !attrDisabled &&
            !isPhotoAlbumBg &&
            !isVideoWatchfacce &&
            !isSlotFollowing &&
            onClickAlign(item.align)
        "
      >
        <a-tooltip :title="attrDisabled ? '' : item.tooltip">
          <Icon :icon="item.icon" :width="24" :height="24" fill="none" />
        </a-tooltip>
      </div>
    </div>

    <!-- x y w h -->
    <div class="mt-2.5 flex justify-between">
      <div
        v-for="(item0, index0) in [
          [layoutAttrs[2], layoutAttrs[3]],
          [layoutAttrs[0], layoutAttrs[1]]
        ]"
        :key="index0"
        class="h-25 bg-black-card p-5 w-34.5 rounded-lg grid grid-rows-2 gap-5 relative"
      >
        <div
          v-for="item in item0"
          :key="item.key"
          class="flex items-center justify-between w-full"
        >
          <AttrInputNumber
            :label="item.label"
            :value="item.value"
            :disabled="item.disabled"
            :width="80"
            :step="1"
            :max="['x', 'y'].includes(item.key) ? shape.maxXY : 9999"
            :min="['x', 'y'].includes(item.key) ? shape.minXY : 0"
            addon-after="px"
            :precision="0"
            compact
            @change="(val: number) => onChange(item.key, val)"
          />
        </div>
      </div>
    </div>
    <!-- 缩放 -->
    <template v-if="canuseScale(shape) || shape.type == ShapeType.Image">
      <AttrInputNumber
        class="mt-2.5"
        :value="shape.isPointerImage ? 100 : scalePercent"
        label="缩放"
        addon-after="%"
        :max="scaleMaxMin.max * 100"
        :min="scaleMaxMin.min * 100"
        :precision="0"
        :disabled="!!attrDisabled || shape.isPointerImage"
        :tooltip="`可在${scaleMaxMin.min * 100}%-${
          scaleMaxMin.max * 100
        }%之间调整，缩放值超过100%时，图片会模糊，请注意！`"
        @change="(v: number) => onScaleChange(v)"
      />
    </template>

    <!-- 透明度 -->
    <template v-if="canuseAlpha(shape) || shape.type == ShapeType.Image">
      <AttrInputNumber
        class="mt-2.5"
        :value="shape.isPointerImage ? 0 : alphaPercent"
        label="透明度"
        addon-after="%"
        :max="100"
        :min="0"
        :precision="0"
        :disabled="alphaDisabled"
        :tooltip="alphaDisabled ? '当前图片不支持设置透明度' : ''"
        @change="onAlphaChange"
      />
    </template>

    <!-- rotation -->
    <template v-if="rotation.show">
      <AttrInputNumber
        class="mt-2.5"
        :value="shape.attrs['rotation']"
        label="旋转"
        addon-after="°"
        :max="720"
        :disabled="!!rotation.disabled"
        @change="(v: number) => onChange('rotation', v)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useGlobalShortcuts, useShapeTransform } from '@/hooks';
import KonvaService from '@/services/konva';
import { Shape, ShapeType } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useRuntimeStore } from '@/store/use-runtime-store';
// import { useRuntimeStore } from '@/store/use-runtime-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { round } from 'lodash-es';
import { PropType, computed, watch, nextTick } from 'vue';
import showToast from '../common/toast';
import { useSlotFollow } from '@/hooks/use-slot-follow';
import { notification } from 'ant-design-vue';

const watchfaceStore = useWatchfaceStore();
const editorStore = useEditorStore();
const runtimeStore = useRuntimeStore();
const { judgeIfFollowingSlot } = useSlotFollow();

type AttrKey = 'x' | 'y' | 'w' | 'h' | 'rotation';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const attrDisabled = computed(() => editorStore.attrDisabled);
const isPhotoAlbumBg = computed(
  () => props.shape.type == ShapeType.PhotoAlbumBg
);
const isVideoWatchfacce = computed(
  () => props.shape.type == ShapeType.VideoWatchface
);

const shapeState = computed(() => runtimeStore.shapeState(props.shape.id));

const followingSlotId = computed(
  () => props.shape.attrs.slotFollowingId ?? undefined
);
const isSlotFollowing = computed(
  () =>
    props.shape.type === ShapeType.Slot &&
    judgeIfFollowingSlot(followingSlotId.value)
);

const rotation = computed(() => {
  const shape = props.shape;
  const type = shape?.type;
  let show = false;
  let disabled = false;

  switch (type) {
    case ShapeType.Text:
      show = true;
      disabled = shape.attrs.style != 'normal';
      break;
    case ShapeType.TextWatchface:
      show = false;
      disabled = shape.attrs.style != 'normal';
      break;
    case ShapeType.ImageArray:
      show = !!canUse('image-array-rotation').valid;
      disabled = !!shape.widgetImageArray;
      break;
  }

  return {
    show,
    disabled: disabled || !!attrDisabled.value || !!isPhotoAlbumBg.value
  };
});

const layoutAttrs = computed(() => {
  const shape = props.shape;
  const type = shape?.type;
  if (!type) return [];

  const _disabled =
    !!attrDisabled.value || isPhotoAlbumBg.value || isVideoWatchfacce.value;
  // widget 的 宽高禁用
  const widgetSupportWH =
    shape.type === ShapeType.Widget && !canUse('widget-flex').valid;

  const resizeEnabled = shape.resizeEnabled();
  const draggableResult = shape.draggable(); // 返回 { valid: false, msg: '容器内数据项不可修改位置' }
  // 需要检查 valid 属性
  const draggable = draggableResult?.valid ?? false;

  // 有旋转时根据 attrs(x,y,w,h,rotation) 计算旋转后包围盒，随旋转角度即时更新；与是否在组内/容器内无关（attrs 已是相对坐标）
  const rotationDeg = Number(shape.attrs.rotation ?? 0);
  const hasRotation = rotationDeg !== 0 && rotationDeg % 360 !== 0;
  const baseX = shape.attrs.x ?? 0;
  const baseY = shape.attrs.y ?? 0;
  let calculateSize: {
    width:number,
    height: number
  } | undefined = undefined;
  // 没有计算过宽高值存储在runtimeStore中图组，此时重新计算
  if (!shapeState.value && shape.type === ShapeType.ImageArray) {
    try {
      // 优先使用当前配色，回退到资源中的第一个配色
      const colorId =
        editorStore.themeColor?.id ??
        Object.keys(shape.attrs.resources?.images ?? {})[0];

      if (colorId) {
        calculateSize = shape.getImageSize('images', colorId);
      }
    } catch (e) {}
  }
  const baseW = resizeEnabled
    ? (shape.attrs.w ?? 0)
    : type === ShapeType.Slot
    ? (shape.attrs.w ?? round(shapeState.value?.width ?? 0))
    : round(shapeState.value?.width ?? calculateSize?.width ?? shape.attrs.w ?? 0);
  const baseH = resizeEnabled
    ? (shape.attrs.h ?? 0)
    : type === ShapeType.Slot
    ? (shape.attrs.h ?? round(shapeState.value?.height ?? 0))
    : round(shapeState.value?.height ?? calculateSize?.height ?? shape.attrs.h ?? 0);

  let displayX = baseX;
  let displayY = baseY;
  let displayW = baseW;
  let displayH = baseH;
  if (hasRotation && baseW > 0 && baseH > 0) {
    const rad = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const cx = baseX + baseW / 2;
    const cy = baseY + baseH / 2;
    const corners = [
      [baseX, baseY],
      [baseX + baseW, baseY],
      [baseX + baseW, baseY + baseH],
      [baseX, baseY + baseH]
    ].map(([px, py]) => {
      const dx = px - cx;
      const dy = py - cy;
      return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
    });
    const xs = corners.map((p) => p[0]);
    const ys = corners.map((p) => p[1]);
    displayX = Math.min(...xs);
    displayY = Math.min(...ys);
    displayW = Math.max(...xs) - displayX;
    displayH = Math.max(...ys) - displayY;
  }

  displayX = round(displayX);
  displayY = round(displayY);
  displayW = round(displayW);
  displayH = round(displayH);

  const attrs: {
    key: AttrKey;
    label: string;
    disabled?: boolean;
    value: number;
  }[] = [
    {
      key: 'x',
      label: 'X',
      disabled: !draggable || _disabled,
      value: round(baseX)
    },
    {
      key: 'y',
      label: 'Y',
      disabled: !draggable || _disabled,
      value: round(baseY)
    },
    {
      key: 'w',
      label: 'W',
      disabled: !resizeEnabled || _disabled || widgetSupportWH,
      value: displayW
    },
    {
      key: 'h',
      label: 'H',
      disabled: !resizeEnabled || _disabled || widgetSupportWH,
      value: displayH
    }
  ];

  return attrs;
});

type Align = 'left' | 'right' | 'x-center' | 'top' | 'bottom' | 'y-center';
const alignItems: { icon: string; align: Align; tooltip: string }[] = [
  { icon: 'align_1.svg', align: 'left', tooltip: '左对齐' },
  { icon: 'align_2.svg', align: 'x-center', tooltip: '水平居中' },
  { icon: 'align_3.svg', align: 'right', tooltip: '右对齐' },
  { icon: 'align_4.svg', align: 'top', tooltip: '顶部对齐' },
  { icon: 'align_5.svg', align: 'y-center', tooltip: '垂直居中' },
  { icon: 'align_6.svg', align: 'bottom', tooltip: '底部对齐' }
];
const onClickAlign = (align: Align) => {
  const shape = props.shape;
  const id = shape.id;
  const parent = shape?.parent;
  const service = KonvaService.getIns();
  if (!shape) return;

  const w0 = parent
    ? (service?.getClientRect(parent.id)?.width ?? parent?.attrs.w ?? 0)
    : watchfaceStore.size.width;
  const h0 = parent
    ? (service?.getClientRect(parent.id)?.height ?? parent?.attrs.h ?? 0)
    : watchfaceStore.size.height;

  const w = service?.getClientRect(id)?.width ?? shape.attrs.w ?? 0;
  const h = service?.getClientRect(id)?.height ?? shape.attrs.h ?? 0;

  let _x: number | undefined;
  let _y: number | undefined;

  switch (align) {
    case 'left':
      _x = 0;
      break;
    case 'x-center':
      _x = w0 / 2 - w / 2;
      break;
    case 'right':
      _x = w0 - w;
      break;

    case 'top':
      _y = 0;
      break;
    case 'y-center':
      _y = h0 / 2 - h / 2;
      break;
    case 'bottom':
      _y = h0 - h;
      break;
    default:
      break;
  }

  // 如果是图组，需要考虑图组的宽高
  if (
    _x != undefined &&
    [ShapeType.ImageArray].includes(shape.type) &&
    !shape.widgetImageArray
  ) {
    const { align = 'left' } = shape.attrs;
    if (align == 'right') {
      _x = _x + w;
    }
    if (align == 'center') {
      _x = _x + w / 2;
    }
  }

  if (_x !== undefined && _x != shape.attrs.x)
    shape.setAttrs({ x: Math.round(_x) });
  if (_y !== undefined && _y != shape.attrs.y)
    shape.setAttrs({ y: Math.round(_y) });
};

// 对齐快捷键
useGlobalShortcuts([
  { keyboardKeys: ['Alt', 'A'], handler: () => onClickAlign('left') },
  { keyboardKeys: ['Alt', 'H'], handler: () => onClickAlign('x-center') },
  { keyboardKeys: ['Alt', 'D'], handler: () => onClickAlign('right') },
  { keyboardKeys: ['Alt', 'W'], handler: () => onClickAlign('top') },
  { keyboardKeys: ['Alt', 'V'], handler: () => onClickAlign('y-center') },
  { keyboardKeys: ['Alt', 'S'], handler: () => onClickAlign('bottom') }
]);

const onChange = (attr: AttrKey, val: number) => {
  // 校验
  if (attr === 'rotation') {
    const valid = canUse('rotation-cannotbe-negative').valid;
    if (!valid && val < 0) {
      return showToast('旋转角度不可为负');
    }
  }

  // Widget 手动编辑 w/h 时，不允许小于 runtime 渲染尺寸（画布会回弹，attrs 应保持一致）
  if (
    (attr === 'w' || attr === 'h') &&
    props.shape?.type === ShapeType.Widget
  ) {
    const state = shapeState.value;
    if (state) {
      const minW = Math.round(state.width ?? 0);
      const minH = Math.round(state.height ?? 0);
      const clampedVal =
        attr === 'w' ? Math.max(val, minW) : Math.max(val, minH);
      if (clampedVal !== val) {
        notification.warning({
          key: 'widget-size-clamp',
          message: '设置失败',
          description: `组的${attr === 'w' ? '宽度' : '高度'}不能小于子元素实际渲染尺寸 ${clampedVal}px`
        });
        // 先写入用户值触发一次变化，再立即写回 clamp 值，确保输入框刷新
        props.shape?.setAttrs({ [attr]: val });
        nextTick(() => {
          props.shape?.setAttrs({ [attr]: clampedVal });
        });
        return;
      }
    }
  }

  // 校验通过
  props.shape?.setAttrs({ [attr]: val });
};

// 缩放/透明度
const {
  canuseScale,
  setScale,
  scaleMaxMin,
  canuseAlpha,
  disableAlpha,
  setAlpha
} = useShapeTransform();
const scalePercent = computed(() => {
  const scale = props.shape.attrs.scale ?? 1;
  return scale * 100;
});
const onScaleChange = (v: number) => {
  const scale = v / 100;
  setScale(props.shape, scale, { isSetSingleScale: true });
};
const alphaDisabled = computed(
  () =>
    !!editorStore.attrDisabled ||
    isPhotoAlbumBg.value ||
    props.shape.isPointerImage ||
    disableAlpha({
      w: layoutAttrs.value[2].value,
      h: layoutAttrs.value[3].value
    })
);
const alphaPercent = computed(() => {
  const alpha = props.shape.attrs.alpha ?? 1;
  return 100 - alpha * 100;
});
const onAlphaChange = (v: number) => {
  const alpha = (100 - v) / 100;
  setAlpha(props.shape, alpha);
};
watch(alphaDisabled, (newVal) => {
  // 如果透明度被禁用则重置透明度为0
  if (newVal) {
    onAlphaChange(0);
  }
});
</script>

<style scoped lang="less">
:deep(.ant-input-number-input) {
  text-align: end;
  padding-right: 1px;
}
:deep(.ant-input-number-group-addon) {
  padding: 0;
  border: none;
  font-size: 0.875rem;
}
.link-bg {
  background: url(../../assets/icons/rect.svg) no-repeat;
  background-size: 100% 100%;
}
</style>
